import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Paper,
  Typography,
  Grid,
  Button,
  TextField,
  Autocomplete,
  CircularProgress,
  Chip,
  LinearProgress,
} from '@mui/material';
import {
  Egg as EggIcon,
  ShoppingCart as SoldIcon,
  Payments as RevenueIcon,
  Inventory2 as StockIcon,
  WarningAmber as BrokenIcon,
  TrendingUp as TrendUpIcon,
  TrendingDown as TrendDownIcon,
  TrendingFlat as TrendFlatIcon,
  Lightbulb as InsightIcon,
} from '@mui/icons-material';
import {
  AreaChart,
  Area,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  ResponsiveContainer,
  ReferenceLine,
  Cell,
  Legend,
} from 'recharts';
import { useQuery } from '@tanstack/react-query';
import { useCurrentFinancialCycle } from '../hooks/useFinancialCycles';
import { useBirdProcurements } from '../hooks/useBirds';
import axiosInstance from '../services/axiosInstance';
import { API_ENDPOINTS } from '../config/api';
import { BirdProcurement } from '../types';

// ── Types ─────────────────────────────────────────────────────────────────────

interface TimelineRow {
  period: string;
  good_eggs: number;
  broken_eggs: number;
  crates_produced: number;
  eggs_sold: number;
  crates_sold: number;
  revenue: number;
  sell_through_rate: number | null;
  cumulative_surplus: number;
}

interface BatchRow {
  procurement_id: number | null;
  batch_name: string;
  good_eggs: number;
  broken_eggs: number;
  eggs_sold: number;
  crates_sold: number;
  revenue: number;
  sell_through_rate: number | null;
}

interface Summary {
  total_good_eggs: number;
  total_broken_eggs: number;
  total_eggs_sold: number;
  total_crates_sold: number;
  unsold_eggs: number;
  unsold_crates: number;
  overall_sell_through_rate: number;
  total_revenue: number;
  avg_revenue_per_crate: number;
}

interface CorrelationData {
  timeline: TimelineRow[];
  summary: Summary;
  by_batch: BatchRow[];
  meta: { date_from: string; date_to: string; period: string; cycle_name: string | null };
}

type Period = 'daily' | 'weekly' | 'monthly';

// ── Formatters ────────────────────────────────────────────────────────────────

const fmtEggs = (n: number) => n.toLocaleString();
const fmtCcy = (n: number) =>
  `GH₵ ${n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const fmtK = (v: number) => {
  const a = Math.abs(v);
  if (a >= 1_000_000) return `${(v / 1_000_000).toFixed(1)}M`;
  if (a >= 1_000) return `${(v / 1_000).toFixed(0)}k`;
  return `${v.toFixed(0)}`;
};
const fmtCcy_K = (v: number) => {
  const a = Math.abs(v);
  if (a >= 1_000_000) return `GH₵${(v / 1_000_000).toFixed(1)}M`;
  if (a >= 1_000) return `GH₵${(v / 1_000).toFixed(0)}k`;
  return `GH₵${v.toFixed(0)}`;
};

const sellThroughColor = (rate: number | null) => {
  if (rate === null) return 'rgba(255,255,255,0.3)';
  if (rate >= 90) return '#66bb6a';
  if (rate >= 70) return '#ff9800';
  return '#f44336';
};

const tickLabel = (period: Period, value: string) => {
  if (period === 'daily') return value.slice(5);
  if (period === 'weekly') return `Wk${value.split('-')[1]}`;
  return value;
};

// ── Stat Card ─────────────────────────────────────────────────────────────────

const StatCard: React.FC<{
  label: string;
  value: string;
  sub?: string;
  color: string;
  icon: React.ReactNode;
  progress?: number;
  progressColor?: string;
}> = ({ label, value, sub, color, icon, progress, progressColor }) => (
  <Paper
    sx={{
      p: 2.5,
      background: `linear-gradient(135deg, rgba(255,255,255,0.04) 0%, rgba(255,255,255,0.01) 100%)`,
      border: `1px solid ${color}30`,
      borderTop: `3px solid ${color}`,
      position: 'relative',
      overflow: 'hidden',
      height: '100%',
      '&::before': {
        content: '""',
        position: 'absolute',
        top: 0,
        right: 0,
        width: 80,
        height: 80,
        borderRadius: '50%',
        background: `${color}08`,
        transform: 'translate(30%, -30%)',
      },
    }}
  >
    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
      <Typography
        variant="caption"
        sx={{
          color: 'rgba(255,255,255,0.5)',
          letterSpacing: '0.07em',
          textTransform: 'uppercase',
          fontSize: '0.68rem',
          fontWeight: 600,
        }}
      >
        {label}
      </Typography>
      <Box sx={{ color, opacity: 0.7, display: 'flex' }}>{icon}</Box>
    </Box>
    <Typography
      variant="h5"
      sx={{ fontWeight: 800, color: '#fff', mt: 0.5, mb: sub ? 0.5 : 0, lineHeight: 1.1 }}
    >
      {value}
    </Typography>
    {sub && (
      <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.45)', fontSize: '0.72rem' }}>
        {sub}
      </Typography>
    )}
    {progress !== undefined && (
      <Box sx={{ mt: 1.5 }}>
        <LinearProgress
          variant="determinate"
          value={Math.min(progress, 100)}
          sx={{
            height: 4,
            borderRadius: 2,
            backgroundColor: 'rgba(255,255,255,0.08)',
            '& .MuiLinearProgress-bar': {
              backgroundColor: progressColor ?? color,
              borderRadius: 2,
            },
          }}
        />
      </Box>
    )}
  </Paper>
);

// ── Insight Strip ─────────────────────────────────────────────────────────────

interface Insight {
  label: string;
  value: string;
  color: string;
  icon: React.ReactNode;
}

const InsightStrip: React.FC<{ insights: Insight[] }> = ({ insights }) => (
  <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap', mb: 3 }}>
    {insights.map((ins, i) => (
      <Paper
        key={i}
        elevation={0}
        sx={{
          px: 2,
          py: 1.25,
          border: `1px solid ${ins.color}25`,
          borderRadius: 2,
          background: `${ins.color}0a`,
          display: 'flex',
          alignItems: 'center',
          gap: 1,
        }}
      >
        <Box sx={{ color: ins.color, display: 'flex', fontSize: 16 }}>{ins.icon}</Box>
        <Box>
          <Typography sx={{ color: 'rgba(255,255,255,0.5)', fontSize: '0.68rem', lineHeight: 1 }}>
            {ins.label}
          </Typography>
          <Typography
            sx={{ color: ins.color, fontSize: '0.8rem', fontWeight: 700, lineHeight: 1.3 }}
          >
            {ins.value}
          </Typography>
        </Box>
      </Paper>
    ))}
  </Box>
);

// ── Tooltips ──────────────────────────────────────────────────────────────────

const ProdSalesTooltip = ({ active, payload, label }: any) => {
  if (!active || !payload?.length) return null;
  return (
    <Paper sx={{ p: 1.5, border: '1px solid rgba(255,152,0,0.25)', minWidth: 180 }}>
      <Typography
        variant="caption"
        sx={{ color: 'rgba(255,255,255,0.5)', display: 'block', mb: 0.75 }}
      >
        {label}
      </Typography>
      {payload.map((p: any) => (
        <Box key={p.dataKey} sx={{ display: 'flex', justifyContent: 'space-between', gap: 2 }}>
          <Typography variant="caption" sx={{ color: p.color }}>
            {p.name}
          </Typography>
          <Typography variant="caption" sx={{ color: '#fff', fontWeight: 600 }}>
            {fmtEggs(p.value)}
          </Typography>
        </Box>
      ))}
    </Paper>
  );
};

const RevenueTooltip = ({ active, payload, label }: any) => {
  if (!active || !payload?.length) return null;
  return (
    <Paper sx={{ p: 1.5, border: '1px solid rgba(255,167,38,0.25)' }}>
      <Typography
        variant="caption"
        sx={{ color: 'rgba(255,255,255,0.5)', display: 'block', mb: 0.5 }}
      >
        {label}
      </Typography>
      <Typography variant="body2" sx={{ color: '#ffa726', fontWeight: 600 }}>
        {fmtCcy(payload[0]?.value ?? 0)}
      </Typography>
    </Paper>
  );
};

const SurplusTooltip = ({ active, payload, label }: any) => {
  if (!active || !payload?.length) return null;
  const val = payload[0]?.value ?? 0;
  return (
    <Paper sx={{ p: 1.5, border: '1px solid rgba(255,152,0,0.25)' }}>
      <Typography
        variant="caption"
        sx={{ color: 'rgba(255,255,255,0.5)', display: 'block', mb: 0.5 }}
      >
        {label}
      </Typography>
      <Typography variant="body2" sx={{ color: val >= 0 ? '#ff9800' : '#f44336', fontWeight: 600 }}>
        {val >= 0 ? 'Surplus' : 'Deficit'}: {fmtEggs(Math.abs(val))} eggs
      </Typography>
    </Paper>
  );
};

const RateTooltip = ({ active, payload, label }: any) => {
  if (!active || !payload?.length) return null;
  const val = payload[0]?.value ?? null;
  return (
    <Paper sx={{ p: 1.5, border: '1px solid rgba(255,152,0,0.25)' }}>
      <Typography
        variant="caption"
        sx={{ color: 'rgba(255,255,255,0.5)', display: 'block', mb: 0.5 }}
      >
        {label}
      </Typography>
      <Typography variant="body2" sx={{ color: sellThroughColor(val), fontWeight: 600 }}>
        {val !== null ? `${val}%` : 'N/A'} sell-through
      </Typography>
    </Paper>
  );
};

// ── Batch Card ────────────────────────────────────────────────────────────────

const BatchCard: React.FC<{ batch: BatchRow }> = ({ batch }) => {
  const rate = batch.sell_through_rate;
  const rateColor = sellThroughColor(rate);
  const soldPct = batch.good_eggs > 0 ? (batch.eggs_sold / batch.good_eggs) * 100 : 0;
  const brokenPct =
    batch.good_eggs + batch.broken_eggs > 0
      ? (batch.broken_eggs / (batch.good_eggs + batch.broken_eggs)) * 100
      : 0;
  const unsold = batch.good_eggs - batch.eggs_sold;

  return (
    <Paper
      sx={{
        p: 2.5,
        border: '1px solid rgba(255,255,255,0.07)',
        background: 'linear-gradient(135deg, rgba(255,255,255,0.03) 0%, transparent 100%)',
        transition: 'border-color 0.2s',
        '&:hover': { borderColor: 'rgba(255,152,0,0.25)' },
      }}
    >
      <Box
        sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}
      >
        <Box>
          <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#fff', lineHeight: 1.2 }}>
            {batch.batch_name}
          </Typography>
          <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.4)' }}>
            {fmtEggs(batch.good_eggs)} eggs produced · {batch.crates_sold.toLocaleString()} crates
            sold
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
          <Chip
            label={rate !== null ? `${rate}%` : '—'}
            size="small"
            sx={{
              bgcolor: `${rateColor}18`,
              color: rateColor,
              fontWeight: 700,
              fontSize: '0.78rem',
              border: `1px solid ${rateColor}30`,
            }}
          />
          <Typography variant="body2" sx={{ color: '#ffa726', fontWeight: 700 }}>
            {fmtCcy(batch.revenue)}
          </Typography>
        </Box>
      </Box>

      {/* Production vs Sold bars */}
      <Box sx={{ mb: 1.5 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
          <Typography
            variant="caption"
            sx={{ color: 'rgba(255,255,255,0.4)', fontSize: '0.68rem' }}
          >
            PRODUCED
          </Typography>
          <Typography
            variant="caption"
            sx={{ color: '#42a5f5', fontSize: '0.68rem', fontWeight: 600 }}
          >
            {fmtEggs(batch.good_eggs)}
          </Typography>
        </Box>
        <Box
          sx={{
            height: 6,
            borderRadius: 3,
            bgcolor: 'rgba(66,165,245,0.15)',
            overflow: 'hidden',
          }}
        >
          <Box sx={{ height: '100%', width: '100%', bgcolor: '#42a5f5', borderRadius: 3 }} />
        </Box>
      </Box>

      <Box sx={{ mb: 2 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
          <Typography
            variant="caption"
            sx={{ color: 'rgba(255,255,255,0.4)', fontSize: '0.68rem' }}
          >
            SOLD
          </Typography>
          <Typography
            variant="caption"
            sx={{ color: rateColor, fontSize: '0.68rem', fontWeight: 600 }}
          >
            {fmtEggs(batch.eggs_sold)}
          </Typography>
        </Box>
        <Box
          sx={{
            height: 6,
            borderRadius: 3,
            bgcolor: 'rgba(255,255,255,0.06)',
            overflow: 'hidden',
          }}
        >
          <Box
            sx={{
              height: '100%',
              width: `${soldPct}%`,
              bgcolor: rateColor,
              borderRadius: 3,
              transition: 'width 0.6s ease',
            }}
          />
        </Box>
      </Box>

      <Box
        sx={{
          display: 'flex',
          gap: 2,
          pt: 1.5,
          borderTop: '1px solid rgba(255,255,255,0.06)',
          flexWrap: 'wrap',
        }}
      >
        <Box>
          <Typography
            variant="caption"
            sx={{ color: 'rgba(255,255,255,0.35)', fontSize: '0.65rem' }}
          >
            UNSOLD
          </Typography>
          <Typography
            variant="body2"
            sx={{ color: unsold > 0 ? '#ff9800' : '#66bb6a', fontWeight: 600 }}
          >
            {fmtEggs(Math.max(0, unsold))} eggs
          </Typography>
        </Box>
        <Box>
          <Typography
            variant="caption"
            sx={{ color: 'rgba(255,255,255,0.35)', fontSize: '0.65rem' }}
          >
            BROKEN
          </Typography>
          <Typography
            variant="body2"
            sx={{ color: brokenPct > 5 ? '#f44336' : 'rgba(255,255,255,0.6)', fontWeight: 600 }}
          >
            {fmtEggs(batch.broken_eggs)} ({brokenPct.toFixed(1)}%)
          </Typography>
        </Box>
        <Box sx={{ ml: 'auto' }}>
          <Typography
            variant="caption"
            sx={{ color: 'rgba(255,255,255,0.35)', fontSize: '0.65rem' }}
          >
            AVG / CRATE
          </Typography>
          <Typography variant="body2" sx={{ color: '#ffa726', fontWeight: 600 }}>
            {batch.crates_sold > 0 ? fmtCcy(batch.revenue / batch.crates_sold) : '—'}
          </Typography>
        </Box>
      </Box>
    </Paper>
  );
};

// ── Chart section wrapper ─────────────────────────────────────────────────────

const ChartSection: React.FC<{
  title: string;
  subtitle?: string;
  children: React.ReactNode;
  height?: number;
}> = ({ title, subtitle, children, height = 260 }) => (
  <Paper
    sx={{
      p: 2.5,
      border: '1px solid rgba(255,255,255,0.07)',
      background: 'linear-gradient(160deg, rgba(255,255,255,0.03) 0%, transparent 60%)',
    }}
  >
    <Box sx={{ mb: 2 }}>
      <Typography variant="h6" sx={{ fontWeight: 700, fontSize: '0.95rem' }}>
        {title}
      </Typography>
      {subtitle && (
        <Typography
          variant="caption"
          sx={{ color: 'rgba(255,255,255,0.4)', display: 'block', mt: 0.25 }}
        >
          {subtitle}
        </Typography>
      )}
    </Box>
    <ResponsiveContainer width="100%" height={height}>
      {children as React.ReactElement}
    </ResponsiveContainer>
  </Paper>
);

const AXIS_STYLE = {
  tick: { fill: 'rgba(255,255,255,0.45)', fontSize: 11 },
  axisLine: false as const,
  tickLine: false as const,
};
const GRID_STYLE = { strokeDasharray: '3 3', stroke: 'rgba(255,255,255,0.06)' };

// ── Main Page ─────────────────────────────────────────────────────────────────

const EggCorrelation: React.FC = () => {
  const { data: currentCycle } = useCurrentFinancialCycle();
  const { data: procurementsData } = useBirdProcurements();

  const [period, setPeriod] = useState<Period>('daily');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [selectedProcurement, setSelectedProcurement] = useState<BirdProcurement | null>(null);

  useEffect(() => {
    if (currentCycle && !dateFrom) {
      setDateFrom(currentCycle.start_date);
      setDateTo(currentCycle.end_date ?? new Date().toISOString().slice(0, 10));
    }
  }, [currentCycle]);

  const params: Record<string, string> = { period };
  if (dateFrom) params.date_from = dateFrom;
  if (dateTo) params.date_to = dateTo;
  if (selectedProcurement) params.procurement_id = String(selectedProcurement.id);

  const {
    data: raw,
    isFetching,
    isError,
  } = useQuery<CorrelationData>({
    queryKey: ['eggCorrelation', params],
    queryFn: async () => {
      const res = await axiosInstance.get(API_ENDPOINTS.EGG_PRODUCTIONS.CORRELATION, { params });
      return res.data.data as CorrelationData;
    },
    enabled: !!dateFrom && !!dateTo,
  });

  const summary = raw?.summary;
  const timeline = raw?.timeline ?? [];
  const batches = raw?.by_batch ?? [];

  const chartData = timeline.map((r) => ({
    ...r,
    label: tickLabel(period, r.period),
  }));

  const brokenRate =
    (summary?.total_good_eggs ?? 0) + (summary?.total_broken_eggs ?? 0) > 0
      ? ((summary?.total_broken_eggs ?? 0) /
          ((summary?.total_good_eggs ?? 0) + (summary?.total_broken_eggs ?? 0))) *
        100
      : 0;

  // ── Auto insights ────────────────────────────────────────────────────────────
  const insights = useMemo<Insight[]>(() => {
    if (!timeline.length) return [];
    const withRate = timeline.filter((t) => t.sell_through_rate !== null);
    if (!withRate.length) return [];

    const sorted = [...withRate].sort(
      (a, b) => (b.sell_through_rate ?? 0) - (a.sell_through_rate ?? 0)
    );
    const best = sorted[0];
    const worst = sorted[sorted.length - 1];

    const mid = Math.floor(withRate.length / 2);
    const avgFirst =
      withRate.slice(0, mid).reduce((s, t) => s + (t.sell_through_rate ?? 0), 0) / Math.max(mid, 1);
    const avgSecond =
      withRate.slice(mid).reduce((s, t) => s + (t.sell_through_rate ?? 0), 0) /
      Math.max(withRate.length - mid, 1);
    const trendDelta = avgSecond - avgFirst;

    const peakRevPeriod = [...timeline].sort((a, b) => b.revenue - a.revenue)[0];

    const out: Insight[] = [];

    if (best) {
      out.push({
        label: 'Best sell-through',
        value: `${best.sell_through_rate}% — ${tickLabel(period, best.period)}`,
        color: '#66bb6a',
        icon: <TrendUpIcon fontSize="small" />,
      });
    }
    if (worst && worst.period !== best?.period) {
      out.push({
        label: 'Weakest period',
        value: `${worst.sell_through_rate}% — ${tickLabel(period, worst.period)}`,
        color: (worst.sell_through_rate ?? 0) < 70 ? '#f44336' : '#ff9800',
        icon: <TrendDownIcon fontSize="small" />,
      });
    }
    out.push({
      label:
        trendDelta > 2
          ? 'Improving trend'
          : trendDelta < -2
            ? 'Declining trend'
            : 'Stable performance',
      value: `${trendDelta > 0 ? '+' : ''}${trendDelta.toFixed(1)}% vs earlier in period`,
      color: trendDelta > 2 ? '#66bb6a' : trendDelta < -2 ? '#f44336' : '#ff9800',
      icon:
        trendDelta > 2 ? (
          <TrendUpIcon fontSize="small" />
        ) : trendDelta < -2 ? (
          <TrendDownIcon fontSize="small" />
        ) : (
          <TrendFlatIcon fontSize="small" />
        ),
    });
    if (peakRevPeriod) {
      out.push({
        label: 'Peak revenue period',
        value: `${fmtCcy(peakRevPeriod.revenue)} — ${tickLabel(period, peakRevPeriod.period)}`,
        color: '#ffa726',
        icon: <RevenueIcon fontSize="small" />,
      });
    }

    return out;
  }, [timeline, period]);

  return (
    <Box sx={{ p: 3 }}>
      {/* ── Header ──────────────────────────────────────────────────────────── */}
      <Box sx={{ mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 0.5 }}>
          <EggIcon sx={{ color: '#ff9800', fontSize: 28 }} />
          <Typography
            variant="h4"
            sx={{
              fontWeight: 800,
              background: 'linear-gradient(90deg, #fff 0%, rgba(255,255,255,0.7) 100%)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
            }}
          >
            Egg Production & Sales
          </Typography>
          {isFetching && <CircularProgress size={18} sx={{ color: '#ff9800', ml: 1 }} />}
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
          {raw?.meta.cycle_name && (
            <Chip
              label={raw.meta.cycle_name}
              size="small"
              sx={{ bgcolor: 'rgba(255,152,0,0.12)', color: '#ff9800', fontWeight: 600 }}
            />
          )}
          {dateFrom && dateTo && (
            <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.35)' }}>
              {dateFrom} → {dateTo}
            </Typography>
          )}
          {isError && <Chip label="Failed to load" size="small" color="error" variant="outlined" />}
        </Box>
      </Box>

      {/* ── Filters ─────────────────────────────────────────────────────────── */}
      <Paper
        sx={{
          p: 2,
          mb: 3,
          display: 'flex',
          gap: 2,
          flexWrap: 'wrap',
          alignItems: 'center',
          border: '1px solid rgba(255,255,255,0.07)',
          background: 'rgba(255,255,255,0.02)',
        }}
      >
        <TextField
          label="From"
          type="date"
          size="small"
          value={dateFrom}
          onChange={(e) => setDateFrom(e.target.value)}
          slotProps={{ inputLabel: { shrink: true } }}
          sx={{ width: 155 }}
        />
        <TextField
          label="To"
          type="date"
          size="small"
          value={dateTo}
          onChange={(e) => setDateTo(e.target.value)}
          slotProps={{ inputLabel: { shrink: true } }}
          sx={{ width: 155 }}
        />
        <Autocomplete
          size="small"
          options={procurementsData?.data ?? []}
          value={selectedProcurement}
          onChange={(_, v) => setSelectedProcurement(v)}
          getOptionLabel={(o: BirdProcurement) => o.identifier ?? `Batch #${o.id}`}
          renderInput={(params) => <TextField {...params} label="Batch (all)" />}
          sx={{ width: 210 }}
        />
        <Box sx={{ display: 'flex', gap: 0.5 }}>
          {(['daily', 'weekly', 'monthly'] as Period[]).map((p) => (
            <Button
              key={p}
              size="small"
              onClick={() => {
                setPeriod(p);
                const anchor = dateTo || new Date().toISOString().slice(0, 10);
                const d = new Date(anchor);
                if (p === 'weekly') {
                  d.setDate(d.getDate() - 12 * 7);
                  setDateFrom(d.toISOString().slice(0, 10));
                } else if (p === 'monthly') {
                  d.setMonth(d.getMonth() - 12);
                  setDateFrom(d.toISOString().slice(0, 10));
                }
              }}
              sx={{
                px: 1.5,
                py: 0.5,
                borderRadius: 1.5,
                fontSize: '0.78rem',
                fontWeight: 600,
                ...(period === p
                  ? { bgcolor: '#ff9800', color: '#000', '&:hover': { bgcolor: '#e65100' } }
                  : {
                      color: 'rgba(255,255,255,0.5)',
                      border: '1px solid rgba(255,255,255,0.12)',
                      '&:hover': { bgcolor: 'rgba(255,152,0,0.08)', color: '#ff9800' },
                    }),
              }}
            >
              {p.charAt(0).toUpperCase() + p.slice(1)}
            </Button>
          ))}
        </Box>
      </Paper>

      {/* ── Summary stat cards ───────────────────────────────────────────────── */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid size={{ xs: 12, sm: 6, md: 4, lg: 2 }}>
          <StatCard
            label="Good Eggs"
            value={fmtK(summary?.total_good_eggs ?? 0)}
            sub={`${Math.floor((summary?.total_good_eggs ?? 0) / 30).toLocaleString()} crates`}
            color="#42a5f5"
            icon={<EggIcon fontSize="small" />}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 4, lg: 2 }}>
          <StatCard
            label="Eggs Sold"
            value={fmtK(summary?.total_eggs_sold ?? 0)}
            sub={`${(summary?.total_crates_sold ?? 0).toLocaleString()} crates`}
            color="#66bb6a"
            icon={<SoldIcon fontSize="small" />}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 4, lg: 2 }}>
          <StatCard
            label="Sell-Through"
            value={`${summary?.overall_sell_through_rate ?? 0}%`}
            sub={`${fmtEggs(summary?.unsold_eggs ?? 0)} unsold`}
            color={sellThroughColor(summary?.overall_sell_through_rate ?? null)}
            icon={<TrendUpIcon fontSize="small" />}
            progress={summary?.overall_sell_through_rate ?? 0}
            progressColor={sellThroughColor(summary?.overall_sell_through_rate ?? null)}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 4, lg: 2 }}>
          <StatCard
            label="Revenue"
            value={fmtCcy_K(summary?.total_revenue ?? 0)}
            sub={`${fmtCcy(summary?.avg_revenue_per_crate ?? 0)} / crate`}
            color="#ffa726"
            icon={<RevenueIcon fontSize="small" />}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 4, lg: 2 }}>
          <StatCard
            label="Unsold Stock"
            value={fmtK(summary?.unsold_eggs ?? 0)}
            sub={`${(summary?.unsold_crates ?? 0).toLocaleString()} crates`}
            color={(summary?.unsold_eggs ?? 0) > 0 ? '#ff9800' : '#66bb6a'}
            icon={<StockIcon fontSize="small" />}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 4, lg: 2 }}>
          <StatCard
            label="Broken / Waste"
            value={fmtK(summary?.total_broken_eggs ?? 0)}
            sub={`${brokenRate.toFixed(1)}% of total`}
            color={brokenRate > 5 ? '#f44336' : '#ef9a9a'}
            icon={<BrokenIcon fontSize="small" />}
            progress={brokenRate}
            progressColor={brokenRate > 5 ? '#f44336' : '#ef9a9a'}
          />
        </Grid>
      </Grid>

      {/* ── Insights ────────────────────────────────────────────────────────── */}
      {insights.length > 0 && (
        <Box sx={{ mb: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 1.25 }}>
            <InsightIcon sx={{ fontSize: 14, color: 'rgba(255,255,255,0.3)' }} />
            <Typography
              variant="caption"
              sx={{
                color: 'rgba(255,255,255,0.3)',
                textTransform: 'uppercase',
                letterSpacing: '0.08em',
                fontWeight: 700,
                fontSize: '0.65rem',
              }}
            >
              Insights
            </Typography>
          </Box>
          <InsightStrip insights={insights} />
        </Box>
      )}

      {/* ── Production vs Sales chart ────────────────────────────────────────── */}
      <Box sx={{ mb: 2.5 }}>
        <ChartSection
          title="Production vs Sales"
          subtitle="Good eggs produced compared to eggs sold per period"
          height={280}
        >
          <AreaChart data={chartData} margin={{ top: 4, right: 16, left: 0, bottom: 4 }}>
            <defs>
              <linearGradient id="prodGrad" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor="#42a5f5" stopOpacity={0.3} />
                <stop offset="95%" stopColor="#42a5f5" stopOpacity={0.02} />
              </linearGradient>
              <linearGradient id="soldGrad" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor="#66bb6a" stopOpacity={0.35} />
                <stop offset="95%" stopColor="#66bb6a" stopOpacity={0.02} />
              </linearGradient>
            </defs>
            <CartesianGrid {...GRID_STYLE} />
            <XAxis dataKey="label" {...AXIS_STYLE} />
            <YAxis tickFormatter={fmtK} {...AXIS_STYLE} width={48} />
            <RechartsTooltip content={<ProdSalesTooltip />} />
            <Legend wrapperStyle={{ fontSize: 12, color: 'rgba(255,255,255,0.6)' }} />
            <Area
              type="monotone"
              dataKey="good_eggs"
              name="Good Eggs Produced"
              stroke="#42a5f5"
              fill="url(#prodGrad)"
              strokeWidth={2}
              dot={false}
            />
            <Area
              type="monotone"
              dataKey="eggs_sold"
              name="Eggs Sold"
              stroke="#66bb6a"
              fill="url(#soldGrad)"
              strokeWidth={2}
              dot={false}
            />
          </AreaChart>
        </ChartSection>
      </Box>

      {/* ── Second row: Revenue + Sell-Through ──────────────────────────────── */}
      <Grid container spacing={2.5} sx={{ mb: 2.5 }}>
        <Grid size={{ xs: 12, md: 6 }}>
          <ChartSection
            title="Revenue by Period"
            subtitle="Actual sales revenue per period"
            height={240}
          >
            <BarChart data={chartData} margin={{ top: 4, right: 16, left: 0, bottom: 4 }}>
              <defs>
                <linearGradient id="revGrad" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#ffa726" stopOpacity={0.9} />
                  <stop offset="100%" stopColor="#ff9800" stopOpacity={0.5} />
                </linearGradient>
              </defs>
              <CartesianGrid {...GRID_STYLE} vertical={false} />
              <XAxis dataKey="label" {...AXIS_STYLE} />
              <YAxis tickFormatter={fmtCcy_K} {...AXIS_STYLE} width={62} />
              <RechartsTooltip content={<RevenueTooltip />} />
              <Bar
                dataKey="revenue"
                name="Revenue"
                fill="url(#revGrad)"
                radius={[3, 3, 0, 0]}
                maxBarSize={28}
              />
            </BarChart>
          </ChartSection>
        </Grid>
        <Grid size={{ xs: 12, md: 6 }}>
          <ChartSection
            title="Sell-Through Rate"
            subtitle="Green ≥ 90% · Orange 70–89% · Red < 70%"
            height={240}
          >
            <BarChart data={chartData} margin={{ top: 4, right: 16, left: 0, bottom: 4 }}>
              <CartesianGrid {...GRID_STYLE} vertical={false} />
              <XAxis dataKey="label" {...AXIS_STYLE} />
              <YAxis domain={[0, 100]} tickFormatter={(v) => `${v}%`} {...AXIS_STYLE} width={44} />
              <RechartsTooltip content={<RateTooltip />} />
              <ReferenceLine y={90} stroke="rgba(102,187,106,0.3)" strokeDasharray="4 2" />
              <ReferenceLine y={70} stroke="rgba(255,152,0,0.3)" strokeDasharray="4 2" />
              <Bar
                dataKey="sell_through_rate"
                name="Sell-Through %"
                radius={[3, 3, 0, 0]}
                maxBarSize={28}
              >
                {chartData.map((entry, idx) => (
                  <Cell
                    key={idx}
                    fill={sellThroughColor(entry.sell_through_rate)}
                    fillOpacity={0.85}
                  />
                ))}
              </Bar>
            </BarChart>
          </ChartSection>
        </Grid>
      </Grid>

      {/* ── Stock balance chart ──────────────────────────────────────────────── */}
      <Box sx={{ mb: 2.5 }}>
        <ChartSection
          title="Running Stock Balance"
          subtitle="Cumulative eggs produced minus sold — rising means unsold inventory is building"
          height={220}
        >
          <AreaChart data={chartData} margin={{ top: 4, right: 16, left: 0, bottom: 4 }}>
            <defs>
              <linearGradient id="surplusGrad" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor="#ff9800" stopOpacity={0.3} />
                <stop offset="95%" stopColor="#ff9800" stopOpacity={0.02} />
              </linearGradient>
            </defs>
            <CartesianGrid {...GRID_STYLE} />
            <XAxis dataKey="label" {...AXIS_STYLE} />
            <YAxis tickFormatter={fmtK} {...AXIS_STYLE} width={48} />
            <RechartsTooltip content={<SurplusTooltip />} />
            <ReferenceLine y={0} stroke="rgba(255,255,255,0.2)" strokeDasharray="4 2" />
            <Area
              type="monotone"
              dataKey="cumulative_surplus"
              name="Stock Balance"
              stroke="#ff9800"
              fill="url(#surplusGrad)"
              strokeWidth={2}
              dot={false}
            />
          </AreaChart>
        </ChartSection>
      </Box>

      {/* ── Batch breakdown ──────────────────────────────────────────────────── */}
      {batches.length > 0 && (
        <Box>
          <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1, mb: 2 }}>
            <Typography variant="h6" sx={{ fontWeight: 700 }}>
              Breakdown by Batch
            </Typography>
            <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.35)' }}>
              {batches.length} batch{batches.length !== 1 ? 'es' : ''}
            </Typography>
          </Box>
          <Grid container spacing={2}>
            {batches.map((b, i) => (
              <Grid size={{ xs: 12, sm: 6, lg: 4 }} key={b.procurement_id ?? `unlinked-${i}`}>
                <BatchCard batch={b} />
              </Grid>
            ))}
          </Grid>
        </Box>
      )}
    </Box>
  );
};

export default EggCorrelation;
